import React from "react";
import { Badge, Box, Divider, Image, Paper, Stack, Text } from "@mantine/core";
import moment from "moment";
import { formatCurrency } from "../../utils/cashDrawerUtils";
import { giftCardStatusColor } from "../../utils/giftCardStatus";
import { GiftCardStatus } from "../../types/giftCards";
import styles from "./GiftCardCertificate.module.css";

/**
 * A printable gift-card certificate. A single visual is the source of truth for
 * both the on-screen preview and the printout, so the two never drift:
 *
 * - `variant="screen"` renders a branded Mantine `Paper` (the live preview).
 * - `variant="print"` renders the hidden `.printOnly` subtree the `@media print`
 *   rule reveals as the sole visible page content (mirrors `TransactionReceipt`).
 *
 * Business identity (`businessName` / `businessLogoUrl`) is passed in — the
 * `useGiftCardPrint` hook injects it from `SalonContext` for the print copy, and
 * the post-checkout preview passes it from `useSalon()`. Every field except
 * `businessName`, `code`, and `initialAmount` is optional and simply omitted when
 * absent, so each surface maps only the data it has.
 */
export interface GiftCardCertificateProps {
  businessName: string;
  /** Shown ABOVE the business name when present; omitted when falsy. */
  businessLogoUrl?: string | null;
  code: string;
  initialAmount: number;
  /** Secondary "Balance remaining" line — only shown when below `initialAmount`. */
  balance?: number | null;
  status?: GiftCardStatus;
  purchaserName?: string | null;
  recipientName?: string | null;
  giftMessage?: string | null;
  expiresAt?: string | null;
  purchasedAt?: string | null;
  variant?: "screen" | "print";
  className?: string;
}

const formatDate = (iso?: string | null): string | null =>
  iso ? moment(iso).format("MMM D, YYYY") : null;

/** The non-active states worth stamping on a certificate so a dead card is
 *  never handed out looking valid. `active` intentionally has no stamp. */
const STATUS_STAMP: Partial<Record<GiftCardStatus, string>> = {
  voided: "VOID",
  expired: "EXPIRED",
  depleted: "NO BALANCE",
};

export default function GiftCardCertificate({
  businessName,
  businessLogoUrl,
  code,
  initialAmount,
  balance,
  status,
  purchaserName,
  recipientName,
  giftMessage,
  expiresAt,
  purchasedAt,
  variant = "screen",
  className,
}: GiftCardCertificateProps) {
  const isPrint = variant === "print";

  const purchaser = purchaserName?.trim();
  const recipient = recipientName?.trim();
  const message = giftMessage?.trim();
  const expiry = formatDate(expiresAt);
  const issued = formatDate(purchasedAt);
  const showBalance = typeof balance === "number" && balance < initialAmount;
  const stamp = status ? STATUS_STAMP[status] : undefined;

  const body = (
    <Stack gap="sm" align="center" ta="center">
      {businessLogoUrl ? (
        <Image
          src={businessLogoUrl}
          alt={businessName}
          fit="contain"
          mah={56}
          w="auto"
          className={styles.logo}
        />
      ) : null}
      <Text fw={700} fz="lg">
        {businessName}
      </Text>

      <Divider w="100%" />

      <Text
        fz="xs"
        tt="uppercase"
        c="dimmed"
        style={{ letterSpacing: "0.08em" }}
      >
        Gift Card
      </Text>
      {/* On screen the code is the brand-teal hero; in print it stays near-black
          for maximum legibility on any printer. */}
      <Text
        fw={700}
        fz={28}
        c={isPrint ? undefined : "var(--brand-teal)"}
        className={styles.code}
      >
        {code}
      </Text>

      <Text fw={700} fz="xl">
        {formatCurrency(initialAmount)}
      </Text>
      {showBalance ? (
        <Text size="sm" c="dimmed">
          Balance remaining {formatCurrency(balance as number)}
        </Text>
      ) : null}

      {(purchaser || recipient) && (
        <Text size="sm">
          {purchaser ? `From ${purchaser}` : ""}
          {purchaser && recipient ? "  →  " : ""}
          {recipient ? `To ${recipient}` : ""}
        </Text>
      )}

      {message ? (
        <Text size="sm" fs="italic" c="dimmed">
          &ldquo;{message}&rdquo;
        </Text>
      ) : null}

      {/* Issued first, with the expiry (when the card has one) directly under it. */}
      {issued ? (
        <Text size="xs" c="dimmed">
          Issued {issued}
        </Text>
      ) : null}
      {expiry ? (
        <Text size="xs" c="dimmed">
          Expires {expiry}
        </Text>
      ) : null}

      {stamp ? (
        <Badge color={giftCardStatusColor(status as string)} variant="filled">
          {stamp}
        </Badge>
      ) : null}
    </Stack>
  );

  if (isPrint) {
    return (
      <Box
        className={[styles.printOnly, className].filter(Boolean).join(" ")}
        data-testid="gift-card-certificate"
        aria-hidden
      >
        <Box className={styles.printCard}>{body}</Box>
      </Box>
    );
  }

  return (
    <Paper
      withBorder
      radius="lg"
      p="lg"
      maw={420}
      w="100%"
      mx="auto"
      className={className}
      data-testid="gift-card-certificate"
    >
      {body}
    </Paper>
  );
}
